import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './App.css';

const STATUSES = [
  { id: 'new', label: 'NEW' },
  { id: 'in_progress', label: 'В работе' },
  { id: 'review', label: 'На ревью' },
  { id: 'done', label: 'Готово' },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.id, s.label]));
const STATUS_IDS = STATUSES.map((s) => s.id);
const nextStatusOf = (s) => {
  const i = STATUS_IDS.indexOf(s);
  if (i === -1 || i === STATUS_IDS.length - 1) return null;
  return STATUS_IDS[i + 1];
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

function buildSearchUrl(ctx) {
  if (!ctx?.q) return null;
  const u = new URLSearchParams();
  u.set('q', ctx.q);
  if (ctx.gender) u.set('gender', ctx.gender);
  if (ctx.np_client_id) u.set('user', ctx.np_client_id);
  if (ctx.rerank) u.set('rerank', '1');
  if (Array.isArray(ctx.segment) && ctx.segment.length) u.set('segment', ctx.segment.join(','));
  if (ctx.boost_current_season === false) u.set('boost', '0');
  if (ctx.segment_as_rerank === false) u.set('sar', '0');
  return '/?' + u.toString();
}

function Row({ rec, onUpdate }) {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const change = async (patch) => {
    setBusy(true);
    try {
      const r = await axios.patch(`/api/ratings/${rec.id}`, patch);
      onUpdate(r.data);
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!comment.trim()) return;
    await change({ add_comment: comment.trim() });
    setComment('');
  };

  const ctx = rec.context || {};
  const searchUrl = buildSearchUrl(ctx);
  const next = nextStatusOf(rec.status);

  return (
    <div className="admin-row">
      <div className="left">
        <div className="top">
          <span className="stars">{'★'.repeat(rec.rating)}<span style={{ color: '#ddd' }}>{'★'.repeat(5 - rec.rating)}</span></span>
          <span className="q">«{ctx.q || '—'}»</span>
          <span className={'status-badge ' + rec.status}>{STATUS_LABEL[rec.status] || rec.status}</span>
          <span className="when">{fmtDate(rec.created_at)}</span>
          {searchUrl && (
            <a className="go-btn" href={searchUrl} target="_blank" rel="noopener noreferrer">перейти ↗</a>
          )}
        </div>
        <div className="ctx">
          {ctx.gender && <span className="tag">пол: {ctx.gender}</span>}
          {Array.isArray(ctx.segment) && ctx.segment.length > 0 && <span className="tag">сегмент: {ctx.segment.join(', ')}</span>}
          {ctx.rerank && <span className="tag">rerank</span>}
          {ctx.boost_current_season && <span className="tag">boost_season</span>}
          {ctx.segment_as_rerank && <span className="tag">seg_as_rerank</span>}
          {ctx.np_client_id && <span className="tag">user: {ctx.np_client_id}</span>}
        </div>
        {rec.comment && <div className="user-comment">{rec.comment}</div>}
      </div>
      <div className="right">
        <div className="status-row">
          <select className="status-select" value={rec.status} disabled={busy} onChange={(e) => change({ status: e.target.value })}>
            {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <button
            className="next-status"
            disabled={busy || !next}
            title={next ? `Перевести в "${STATUS_LABEL[next]}"` : 'Финальный статус'}
            onClick={() => next && change({ status: next })}
          >→</button>
        </div>
        <div className="history">
          {(rec.admin_comments || []).map((c, i) => (
            <div key={i} className="h">
              <span className="at">{fmtDate(c.at)}</span>
              {c.text}
            </div>
          ))}
        </div>
        <textarea
          placeholder="Добавить комментарий…"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <button className="send-btn" disabled={busy || !comment.trim()} onClick={send}>Отправить</button>
      </div>
    </div>
  );
}

export default function Admin() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(new Set(STATUS_IDS));
  const [starFilter, setStarFilter] = useState(new Set([1, 2, 3, 4, 5]));
  const [qFilter, setQFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');

  useEffect(() => {
    axios.get('/api/ratings').then((r) => { setList(r.data); setLoading(false); });
  }, []);

  const onUpdate = (rec) => setList((arr) => arr.map((r) => (r.id === rec.id ? rec : r)));

  const filtered = useMemo(() => list.filter((r) => {
    if (!statusFilter.has(r.status)) return false;
    if (!starFilter.has(r.rating)) return false;
    const ctx = r.context || {};
    if (qFilter && !(ctx.q || '').toLowerCase().includes(qFilter.toLowerCase())) return false;
    if (userFilter && String(ctx.np_client_id || '') !== userFilter) return false;
    if (genderFilter && ctx.gender !== genderFilter) return false;
    return true;
  }), [list, statusFilter, starFilter, qFilter, userFilter, genderFilter]);

  const toggleSet = (setter, val) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(val)) next.delete(val); else next.add(val);
    return next;
  });

  return (
    <div className="admin-page">
      <h1>Оценки поиска <span style={{ fontSize: 13, color: '#888', fontWeight: 400 }}>· {filtered.length} из {list.length}</span></h1>

      <div className="admin-filters">
        <label>
          Запрос
          <input type="text" placeholder="часть текста" value={qFilter} onChange={(e) => setQFilter(e.target.value)} />
        </label>
        <label>
          np_client_id
          <input type="text" value={userFilter} onChange={(e) => setUserFilter(e.target.value)} />
        </label>
        <label>
          Пол
          <select value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)}>
            <option value="">все</option>
            <option value="male">male</option>
            <option value="female">female</option>
            <option value="kids">kids</option>
          </select>
        </label>
        <label style={{ minWidth: 220 }}>
          Звёзды
          <div className="status-chips">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                className={'chip' + (starFilter.has(n) ? ' active' : '')}
                onClick={() => toggleSet(setStarFilter, n)}
              >★ {n}</button>
            ))}
          </div>
        </label>
        <label style={{ minWidth: 320 }}>
          Статус
          <div className="status-chips">
            {STATUSES.map((s) => (
              <button
                key={s.id}
                type="button"
                className={'chip' + (statusFilter.has(s.id) ? ' active' : '')}
                onClick={() => toggleSet(setStatusFilter, s.id)}
              >{s.label}</button>
            ))}
          </div>
        </label>
      </div>

      {loading && <div className="status">Загрузка…</div>}
      {!loading && filtered.length === 0 && <div className="status">Нет оценок под выбранные фильтры</div>}
      {!loading && filtered.map((r) => <Row key={r.id} rec={r} onUpdate={onUpdate} />)}
    </div>
  );
}
